/**
 * Shrinks a picked document photo to 2 MB or less before it is read or uploaded.
 *
 * Camera captures and gallery picks both arrive as a plain File from the same
 * <input type="file">, so this one path covers both. Phone photos are 3–9 MB; on a
 * slow uplink that was a 45-second upload, repeated in full on every retry.
 *
 * Legibility comes first. Measured on real Aadhaar cards, a 1400px long edge
 * loses the name and DOB and 2000px keeps only the DOB — 2600px keeps both (see
 * compressImageForVerification in ScreeningDocumentUpload.js). So size is cut by
 * JPEG quality at 2600px, and the image is only made smaller as a last resort.
 * In practice a 12MP photo lands at ~0.4–0.6 MB at the first quality step.
 *
 * Never blocks the candidate: anything this cannot decode (HEIC on Android, a
 * photo too large for an old iPhone's canvas) is returned untouched and the
 * server's own 2 MB safety net (server/utils/documentImageCompression.js) takes over.
 */

export const MAX_STORED_IMAGE_BYTES = 2 * 1024 * 1024;
export const MAX_IMAGE_DIMENSION = 2600;
const FALLBACK_IMAGE_DIMENSION = 2000;
const QUALITY_STEPS = [0.88, 0.82, 0.76, 0.7, 0.64];
// A decode that never settles must not strand the candidate on the document step.
const DECODE_TIMEOUT_MS = 15000;
// Types every browser canvas can decode. HEIC/HEIF is deliberately absent.
const DECODABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Long edge capped at maxDimension, aspect ratio kept, never enlarged. */
export function fitWithin(width, height, maxDimension) {
  const longEdge = Math.max(width, height);
  if (!longEdge || longEdge <= maxDimension) return { width, height };
  const scale = maxDimension / longEdge;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export function shouldCompress(file, maxBytes = MAX_STORED_IMAGE_BYTES) {
  return !!file && file.size > maxBytes && DECODABLE_TYPES.includes(String(file.type).toLowerCase());
}

// Chrome 81+ / Safari 13.1+ apply EXIF orientation when decoding for canvas, so the
// pixels drawn are already upright. Rotating by hand here would rotate twice.
function withTimeout(promise, ms) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('decode timed out')), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close?.() };
    } catch (_) {
      // Fall through to <img>, which decodes some files createImageBitmap refuses.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

function toJpegBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

async function encodeUnderLimit(decoded, maxDimension, maxBytes) {
  const { width, height } = fitWithin(decoded.width, decoded.height, maxDimension);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  try {
    const ctx = canvas.getContext('2d');
    // A transparent PNG screenshot would otherwise turn black where it was clear.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(decoded.source, 0, 0, width, height);
    for (const quality of QUALITY_STEPS) {
      const blob = await toJpegBlob(canvas, quality);
      if (blob && blob.size <= maxBytes) return { blob, width, height, quality };
    }
    return null;
  } finally {
    // iOS Safari keeps canvas memory alive until it is resized to nothing; a few
    // un-released 2600px canvases are enough to make later draws come out blank.
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * @returns {Promise<{file: File, compressed: boolean, originalBytes: number,
 *   width?: number, height?: number, quality?: number}>}
 */
export async function fitImageForStorage(
  file,
  { maxBytes = MAX_STORED_IMAGE_BYTES, decodeTimeoutMs = DECODE_TIMEOUT_MS } = {}
) {
  const originalBytes = file?.size || 0;
  const unchanged = { file, compressed: false, originalBytes };
  if (!shouldCompress(file, maxBytes)) return unchanged;

  let decoded;
  const decoding = decode(file);
  try {
    decoded = await withTimeout(decoding, decodeTimeoutMs);
    for (const maxDimension of [MAX_IMAGE_DIMENSION, FALLBACK_IMAGE_DIMENSION]) {
      const out = await encodeUnderLimit(decoded, maxDimension, maxBytes);
      if (out) {
        const name = String(file.name || 'document').replace(/\.[^.]*$/, '') + '.jpg';
        return {
          file: new File([out.blob], name, { type: 'image/jpeg', lastModified: Date.now() }),
          compressed: true,
          originalBytes,
          width: out.width,
          height: out.height,
          quality: out.quality,
        };
      }
    }
    return unchanged;
  } catch (_) {
    // A decode that lost the race may still finish later; release it when it does.
    if (!decoded) decoding.then((d) => d.release()).catch(() => {});
    return unchanged;
  } finally {
    decoded?.release();
  }
}
